// Streaming tool-use loop for the Strudel coding agent.
//
// Architecture:
//   - Uses the plain Messages API (not the beta tool runner) so we can
//     stream text deltas into the UI and still run the tool loop by
//     hand. The loop is simple: send → stream → on tool_use, execute
//     handlers → append tool_results → repeat until stop_reason !== 'tool_use'.
//   - System prompt is cached server-side via `cache_control: ephemeral`
//     on the single system block. Turn 2+ reads from cache.
//   - AbortController on the whole turn for user cancellation.
//   - Per-turn budget cap so a misbehaving model can't spin.
//
// Phase 1 is read-only, but the shape here already accommodates writes —
// tool handlers return arbitrary JSON, and the loop doesn't care.

import Anthropic from '@anthropic-ai/sdk';
import type { AgentBridge } from './bridge';
import { clearBreakpoint, markBreakpoint } from './cacheBreakpoints';
import { diffRange } from './diff';
import {
  capabilitiesFor,
  DEFAULT_MODEL,
  type EffortLevel,
  type ModelId,
} from './storage';
import { buildSystemPrompt, type SystemProfile } from './systemPrompt';
import { buildTools, type AgentTool } from './tools';

const MAX_TOKENS = 64000;
export const DEFAULT_TOOL_BUDGET = 30;

export type ChatMessage =
  | { role: 'user'; content: string }
  | { role: 'assistant'; content: string };

export type ToolCallTrace = {
  id: string;
  name: string;
  input: unknown;
  /** 'applied' for read tools and approved writes; 'skipped' when the
   *  user declined a write-tool card; 'failed' when the handler errored. */
  status: 'applied' | 'skipped' | 'failed';
  /** True when the tool's success mutates the Song AST (and therefore
   *  lands on the undo stack). Transport-only tools like play/stop
   *  report false. Populated from AgentTool.mutatesSong — single source
   *  of truth, no drift. */
  mutatesSong: boolean;
  result: unknown;
};

export type PendingToolCall = {
  id: string;
  name: string;
  input: unknown;
};

export type UsageSummary = {
  /** Uncached input tokens — billed at full rate. */
  inputTokens: number;
  /** Tokens served from the prompt cache — billed at ~0.1×. */
  cacheReadTokens: number;
  /** Tokens written to the cache this turn — billed at ~1.25×. */
  cacheWriteTokens: number;
  /** Output tokens. */
  outputTokens: number;
};

export type StreamHandlers = {
  /** Called repeatedly with incremental text deltas. */
  onTextDelta?: (delta: string) => void;
  /** Called for *every* tool call once it has been applied or skipped. */
  onToolCall?: (trace: ToolCallTrace) => void;
  /** Approval gate for write tools. Resolving to 'apply' runs the
   *  handler; 'skip' returns a structured tool_result telling the model
   *  the user declined so it can adapt. If omitted, writes auto-apply
   *  (matches read-tool behaviour; useful for tests and autonomous mode
   *  in a later phase). */
  onWriteDecision?: (pending: PendingToolCall) => Promise<'apply' | 'skip'>;
  /** Called once when the turn finishes cleanly (stop_reason end_turn). */
  onComplete?: (finalText: string) => void;
  /** Called on aborts, API errors, or exceeded budgets. */
  onError?: (error: Error) => void;
  /** Aggregate usage across every iteration of the tool-use loop,
   *  emitted once when the turn settles (complete OR error). */
  onUsage?: (usage: UsageSummary) => void;
};

export type SendOptions = {
  apiKey: string;
  history: ChatMessage[];
  userMessage: string;
  bridge: AgentBridge;
  signal?: AbortSignal;
  handlers?: StreamHandlers;
  /** Cap on tool-call iterations per turn. Defaults to DEFAULT_TOOL_BUDGET. */
  toolBudget?: number;
  /** Model to use. Defaults to DEFAULT_MODEL. Changing the model mid-
   *  conversation invalidates prompt caching on the next turn. */
  model?: ModelId;
  /** Effort level — applies to models that support `output_config.effort`.
   *  'off' disables thinking + effort entirely (cheapest). Ignored for
   *  Haiku. If omitted, falls back to the model's default. */
  effort?: EffortLevel | null;
  /** Which system-prompt variant to send. 'minimal' drops idiom corpus
   *  and few-shot — chosen by the router for inspect/edit intents. */
  systemProfile?: SystemProfile;
};

export async function sendTurn(opts: SendOptions): Promise<void> {
  const { apiKey, history, userMessage, bridge, signal, handlers } = opts;
  const budget =
    typeof opts.toolBudget === 'number' && opts.toolBudget > 0
      ? Math.floor(opts.toolBudget)
      : DEFAULT_TOOL_BUDGET;
  const model = opts.model ?? DEFAULT_MODEL;
  const caps = capabilitiesFor(model);
  // Clamp effort to what the model supports; null when effort is off.
  const effort =
    opts.effort && caps.efforts.includes(opts.effort)
      ? opts.effort
      : caps.defaultEffort;
  const client = new Anthropic({
    apiKey,
    // Required for browser-side calls. The user's key never leaves the
    // browser; they've opted in by entering it in Settings.
    dangerouslyAllowBrowser: true,
  });

  const tools = buildTools(bridge);
  const toolMap = new Map<string, AgentTool>();
  for (const t of tools) toolMap.set(t.definition.name, t);

  // Compute the system prompt ONCE. Byte-identical across iterations is
  // a caching invariant (see systemPrompt.test.ts); hoisting makes it
  // clear the same string is reused, and prevents a future edit from
  // accidentally introducing per-iteration variance.
  const systemPromptText = buildSystemPrompt(opts.systemProfile ?? 'compose');

  const messages: Anthropic.MessageParam[] = [
    ...history.map(
      (m): Anthropic.MessageParam => ({
        role: m.role,
        content: m.content,
      }),
    ),
    { role: 'user', content: userMessage },
  ];

  // Inter-turn cache: mark the last *history* message so turn N+1
  // hits cache on everything from prior turns. Skipped on turn 1 —
  // there's no prior conversation to cache yet.
  if (history.length > 0) {
    markBreakpoint(messages[history.length - 1]!);
  }
  // Within-turn anchor: mark the new user message so iterations 2+
  // of this turn cache through the user input + any attached editor
  // selection (which can be hundreds of tokens and otherwise re-pays
  // every iteration).
  markBreakpoint(messages[messages.length - 1]!);

  // Sliding breakpoint pointer: tracks the index of the most recent
  // tool_results message that carries cache_control. Each new push
  // strips the prior one to stay under the 4-breakpoint per-request
  // cap (system + history-end + user + sliding = 4 max).
  let slidingBreakpointIdx: number | null = null;

  let collectedText = '';
  let toolCallCount = 0;
  const usage: UsageSummary = {
    inputTokens: 0,
    cacheReadTokens: 0,
    cacheWriteTokens: 0,
    outputTokens: 0,
  };

  // Build request-shape fragments based on model capability. Adaptive
  // thinking on models that support it; output_config.effort where the
  // model allows it. Both are omitted cleanly for Haiku so we don't 400.
  // effort='off' is the cheap path: disables both thinking and the
  // output_config.effort knob — used by the router for inspect/edit
  // intents where extra thinking burns output tokens for no gain.
  const thinking =
    caps.adaptiveThinking && effort !== 'off'
      ? { type: 'adaptive' as const }
      : undefined;
  const outputConfig = effort && effort !== 'off' ? { effort } : undefined;

  try {
    // Manual tool-use loop. Each iteration sends the full messages
    // array and either streams a final answer (end_turn) or picks up
    // tool_use blocks to execute.
    for (let iteration = 0; iteration < budget + 1; iteration++) {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

      const stream = client.messages.stream(
        {
          model,
          max_tokens: MAX_TOKENS,
          system: [
            {
              type: 'text',
              text: systemPromptText,
              cache_control: { type: 'ephemeral' },
            },
          ],
          tools: tools.map((t) => t.definition),
          messages,
          ...(thinking ? { thinking } : {}),
          ...(outputConfig ? { output_config: outputConfig } : {}),
        },
        { signal },
      );

      stream.on('text', (delta) => {
        collectedText += delta;
        handlers?.onTextDelta?.(delta);
      });

      const finalMessage = await stream.finalMessage();

      // Accumulate usage across every iteration in the tool-use loop
      // — a multi-step turn burns multiple model requests.
      const u = finalMessage.usage;
      if (u) {
        usage.inputTokens += u.input_tokens ?? 0;
        usage.cacheReadTokens += u.cache_read_input_tokens ?? 0;
        usage.cacheWriteTokens += u.cache_creation_input_tokens ?? 0;
        usage.outputTokens += u.output_tokens ?? 0;
      }

      // Record the assistant turn verbatim — preserves tool_use blocks
      // that the follow-up tool_result events need to reference.
      messages.push({ role: 'assistant', content: finalMessage.content });

      if (finalMessage.stop_reason === 'end_turn') {
        handlers?.onComplete?.(collectedText);
        handlers?.onUsage?.(usage);
        return;
      }

      if (finalMessage.stop_reason !== 'tool_use') {
        // max_tokens, refusal, pause_turn, etc. Surface as an error
        // so the user sees why we stopped; collectedText is already
        // displayed via the onTextDelta stream.
        if (finalMessage.stop_reason === 'max_tokens') {
          throw new Error(
            `Response truncated — output hit the per-turn token budget (${MAX_TOKENS.toLocaleString()}). Try splitting the task into smaller steps, or raise MAX_TOKENS in src/agent/client.ts.`,
          );
        }
        throw new Error(`Unexpected stop_reason: ${finalMessage.stop_reason}`);
      }

      const toolUses = finalMessage.content.filter(
        (b): b is Anthropic.ToolUseBlock => b.type === 'tool_use',
      );
      if (toolUses.length === 0) {
        // Defensive: stop_reason was tool_use but no blocks. Bail.
        handlers?.onComplete?.(collectedText);
        return;
      }

      const toolResults: Anthropic.ToolResultBlockParam[] = [];
      for (const use of toolUses) {
        if (toolCallCount >= budget) {
          throw new Error(
            `Tool-call budget exceeded (${budget} per turn)`,
          );
        }
        toolCallCount += 1;

        const tool = toolMap.get(use.name);
        let result: unknown;
        let status: ToolCallTrace['status'] = 'applied';

        if (!tool) {
          result = { error: `Unknown tool: ${use.name}` };
          status = 'failed';
        } else {
          // Write tools route through the approval gate when one is set.
          if (tool.write && handlers?.onWriteDecision) {
            const decision = await handlers.onWriteDecision({
              id: use.id,
              name: use.name,
              input: use.input,
            });
            if (decision === 'skip') {
              status = 'skipped';
              result = {
                skipped: true,
                reason:
                  'User declined this edit. Do not retry the same change; if ' +
                  'you believe it was still the right move, explain why or ' +
                  'propose an alternative — otherwise move on.',
              };
            }
          }

          if (status !== 'skipped') {
            // Capture pre-code for song-mutating tools so we can flash
            // the changed region after React commits the state update.
            const preCode =
              tool.mutatesSong === true ? bridge.getEditorCode() : null;
            try {
              result = tool.handler(use.input);
              if (
                result &&
                typeof result === 'object' &&
                'error' in (result as object)
              ) {
                status = 'failed';
              }
            } catch (e) {
              result = {
                error: e instanceof Error ? e.message : String(e),
              };
              status = 'failed';
            }
            // On successful AST mutation, wait for React to commit,
            // diff pre/post, and fire the flash. Bounded 1-frame delay.
            if (status === 'applied' && preCode !== null) {
              await new Promise<void>((r) =>
                requestAnimationFrame(() => r()),
              );
              const postCode = bridge.getEditorCode();
              const range = diffRange(preCode, postCode);
              if (range) bridge.flashChange(range.from, range.to);
            }
          }
        }

        handlers?.onToolCall?.({
          id: use.id,
          name: use.name,
          input: use.input,
          status,
          mutatesSong: tool?.mutatesSong === true,
          result,
        });

        toolResults.push({
          type: 'tool_result',
          tool_use_id: use.id,
          content: JSON.stringify(result),
          is_error: status === 'failed' ? true : undefined,
        });
      }

      // Slide the within-turn breakpoint forward: drop the previous
      // tool_results' cache_control (its cached prefix stays valid
      // until 5-min TTL — see cacheBreakpoints.ts) and put a fresh
      // breakpoint on the latest tool_result block. This keeps the
      // cap at 4 cache_control per request as the loop deepens.
      if (slidingBreakpointIdx !== null) {
        clearBreakpoint(messages[slidingBreakpointIdx]!);
      }
      const lastResult = toolResults[toolResults.length - 1];
      if (lastResult) {
        lastResult.cache_control = { type: 'ephemeral' };
      }
      messages.push({ role: 'user', content: toolResults });
      slidingBreakpointIdx = messages.length - 1;
    }

    throw new Error(
      `Exceeded ${budget} tool-call iterations`,
    );
  } catch (e) {
    if (e instanceof Error) {
      handlers?.onError?.(e);
      // Surface whatever usage we accumulated before the error so the
      // user sees the partial cost of the aborted / failed turn.
      handlers?.onUsage?.(usage);
      return;
    }
    handlers?.onError?.(new Error(String(e)));
    handlers?.onUsage?.(usage);
  }
}
